import i18next from 'i18next';

import { config } from '../config';

import axios from '../core/http';

import { APIcaller, APIcallerv2 } from './APIcaller';

const baseApiUrl = `${config.baseApiUrl}/v1`;

function getOrdersOfStores(storeIds) {
  return APIcaller.apiPost('/order/get-by-stores', {
    storeIds,
  });
}

function getOrdersOfStoresPaginate(
  storeIds,
  supplierIds,
  search,
  skip,
  limit,
  orderBy,
  orderType,
  params,
) {
  let requestUri = `${baseApiUrl}/order/orders?`;

  if (skip != null) {
    requestUri = requestUri.concat('skip=', `${skip}&`);
  }

  if (limit != null) {
    requestUri = requestUri.concat('limit=', `${limit}&`);
  }

  if (orderBy != null) {
    requestUri = requestUri.concat('orderBy=', `${orderBy}&`);
  }

  if (orderType != null) {
    requestUri = requestUri.concat('orderType=', `${orderType}&`);
  }

  if (search) {
    requestUri = requestUri.concat('search=', `${search}&`);
  }

  requestUri = requestUri.replace(/(&)$/, '');

  if (params) {
    requestUri = requestUri.concat(params);
  }

  let requestBody = { storeIds, supplierIds };

  return axios
    .post(requestUri, requestBody, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('loopbackAccessToken'),
      },
    })
    .then(function (response) {
      return response.data;
    })
    .catch(function (error) {
      throw error;
    });
}

function getOrdersCountOfStores(storeIds, supplierIds, status, isCentralKitchenCount) {
  return axios
    .post(
      `${baseApiUrl}/order/orders/count`,
      { storeIds, supplierIds, status, isCentralKitchenCount },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('loopbackAccessToken'),
        },
      },
    )
    .then(function (response) {
      return response.data;
    })
    .catch(function (error) {
      throw error;
    });
}

function patchOrder(
  order,
  productOrders,
  isEditingDate,
  productOrdersSPToBeUpdated,
  type,
  sendParams,
) {
  return axios
    .patch(
      baseApiUrl + '/order',
      {
        type,
        order,
        sendParams,
        productOrders,
        isEditingDate,
        productOrdersSPToBeUpdated,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('loopbackAccessToken'),
          sha: process.env.REACT_APP_GIT_SHA,
        },
      },
    )
    .then(function (response) {
      return response.data;
    })
    .catch(function (error) {
      throw error;
    });
}

const getOrdersAnalyticsByStore = async (
  storeIds,
  startDate,
  endDate,
  { getGrossSalaries = false, withNonSalesConsumption = true } = {},
) => {
  try {
    const data = await APIcaller.apiPost('/order/analytics-by-store', {
      storeIds,
      startDate,
      endDate,
      getGrossSalaries,
      withNonSalesConsumption,
    });

    return data;
  } catch (err) {
    throw err;
  }
};

const getTurnoverByStoreIdsAndDates = async (storeIds, startDate, endDate) => {
  try {
    const data = await APIcaller.apiPost('/order/turnover', {
      storeIds,
      startDate,
      endDate,
    });

    return data;
  } catch (err) {
    throw err;
  }
};

function sendOrderByEmail(
  userId,
  storeId,
  supplierId,
  orderId,
  to,
  cc,
  bcc,
  body,
  userTimezone,
  withMail,
) {
  return axios
    .post(baseApiUrl + '/order/handleOrderByEmail', {
      userId,
      storeId,
      supplierId,
      orderId,
      to,
      cc,
      bcc,
      body,
      userTimezone,
      withMail,
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('loopbackAccessToken'),
        sha: process.env.REACT_APP_GIT_SHA,
      },
    })
    .then(function (response) {
      return response.data;
    })
    .catch(function (error) {
      throw error;
    });
}

const deleteOrderById = (id) => APIcaller.apiDelete(`/order/${id}`);

function cancelReceptionOrderById(id) {
  return axios
    .post(`${baseApiUrl}/order/${id}/cancel-reception`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('loopbackAccessToken'),
      },
    })
    .then(function (response) {
      return response.data;
    })
    .catch(function (error) {
      throw error;
    });
}

const getOrdersAnalyticsByCategory = (
  storeIds,
  startDate,
  endDate,
  { withNonSalesConsumption = true } = {},
) =>
  APIcaller.apiPost('/order/analytics-by-category', {
    storeIds,
    startDate,
    endDate,
    withNonSalesConsumption,
  });

const getInventoryForStores = (storeIds, startDate, endDate, showMessage, type, entityId) =>
  axios
    .post(`${baseApiUrl}/admin/stores/inventory`, {
      storeIds,
      startDate,
      endDate,
      type,
      entityId,
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('loopbackAccessToken'),
      },
    })
    .then(function (response) {
      return response.data;
    })
    .catch(function () {
      showMessage(i18next.t('ORDERS.BY_CATEGORY.INVENTORY_DATES_FETCH_FAILURE'), 'error');

      return { list: [] };
    });

function getOrderContentsExportDataByIds(ids) {
  return axios
    .post(`${baseApiUrl}/order/export/content`, {
      ids,
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('loopbackAccessToken'),
      },
    })
    .then(function (response) {
      return response.data;
    })
    .catch(function (error) {
      throw error;
    });
}

function getOrderAggregatedExportDataByIds(ids) {
  return axios
    .post(`${baseApiUrl}/order/export/aggregate`, {
      ids,
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('loopbackAccessToken'),
      },
    })
    .then(function (response) {
      return response.data;
    })
    .catch(function (error) {
      throw error;
    });
}

function getOrderAnomaliesExportDataByIds(ids) {
  return axios
    .post(`${baseApiUrl}/order/export/outliers`, {
      ids,
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('loopbackAccessToken'),
      },
    })
    .then(function (response) {
      return response.data;
    })
    .catch(function (error) {
      throw error;
    });
}

const getRecommandationsByBatch = (storeId, entityIds, endDate, startOrderDate, endOrderDate) =>
  axios
    .post(
      `${baseApiUrl}/order/stores/${storeId}/recommandation`,
      { entityIds },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('loopbackAccessToken'),
        },
        params: {
          endDate,
          startOrderDate,
          endOrderDate,
        },
      },
    )
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });

const sendGroupedOrder = (orderIds, sendParams) =>
  axios
    .post(`${baseApiUrl}/order/group`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('loopbackAccessToken'),
      },
      orderIds,
      sendParams,
    })
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });

function markOrdersAsDraft(orderIds) {
  return axios
    .post(`${baseApiUrl}/order/mark-as-draft`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('loopbackAccessToken'),
      },
      orderIds,
    })
    .then(function (response) {
      return response.data;
    })
    .catch(function (error) {
      throw error;
    });
}

const markPreparationAsPreparing = (orderId, productOrders) =>
  APIcaller.apiPost(`/order/${orderId}/status/in-preparation`, {
    productOrders,
  });

const markPreparationAsPrepared = (orderId, productOrders) =>
  APIcaller.apiPost(`/order/${orderId}/status/prepared`, {
    productOrders,
  });

const getOrdersAnalyticsBySupplierProduct = (
  clientId,
  startDate,
  endDate,
  storeIds,
  supplierIds,
  strategicOnly,
  categoryIds,
  subCategoryIds,
) =>
  APIcaller.apiPost('/order/analytics-by-supplier-products', {
    clientId,
    startDate,
    endDate,
    storeIds,
    supplierIds,
    strategicOnly,
    categoryIds,
    subCategoryIds,
  });

const getOrdersAnalyticsPricesEvolution = (
  clientId,
  startDate,
  endDate,
  storeIds,
  supplierIds,
  strategicOnly,
  categories,
  subCategories,
) =>
  APIcaller.apiPost('/order/analytics/prices', {
    clientId,
    startDate,
    endDate,
    storeIds,
    supplierIds,
    strategicOnly,
    categories,
    subCategories,
  });

const setCreditRequestProcessed = (clientId, orderIds) =>
  APIcaller.apiPost('/order/set-credit-request-processed', {
    clientId,
    orderIds,
  });

const purchaseOrderPreview = (order, productOrders, userTimezone = 'Europe/Paris') =>
  APIcaller.apiPost('/order/generate-purchase-order-preview', {
    order,
    productOrders,
    userTimezone,
  });

const creditOrderPreview = (order, productOrders) =>
  APIcaller.apiPost('/order/generate-credit-order-preview', {
    order,
    productOrders,
  });

const getOrderDetailsByOrderIds = (orderIds, productOrders) =>
  APIcaller.apiPost('/order/export/details', {
    ids: orderIds,
  });

const setOrderToInPreparation = (orderId, productOrders) =>
  APIcaller.apiPost(`/order/${orderId}/status/in-preparation`, {
    productOrders,
  });

const setOrderToPrepared = (orderId, productOrders, receptionComment) =>
  APIcaller.apiPost(`/order/${orderId}/status/prepared`, {
    productOrders,
    receptionComment,
  });

const generateDeliveryNotePreview = (order, productOrders, userTimezone = 'Europe/Paris') =>
  APIcaller.apiPost('/order/generate-delivery-note-preview', {
    order,
    productOrders,
    userTimezone,
  });

const checkCompaniesInformation = (storeId, supplierId) =>
  APIcaller.apiGet('/order/check-companies-information', { storeId, supplierId });

const getOrderBatchCreationTemplate = (clientId, storeIds) =>
  APIcaller.apiPost(
    `/admin/clients/${clientId}/orders/batch-creation/template`,
    {
      storeIds,
    },
    'blob',
  );

const ordersBatchCreationValidation = (clientId, batchOrders) =>
  APIcaller.apiPost(
    `/admin/clients/${clientId}/orders/batch-creation/validate`,
    batchOrders,
    'blob',
  );

const ordersBatchCreation = (clientId, batchOrders) =>
  APIcaller.apiPost(`/admin/clients/${clientId}/orders/batch-creation/import`, batchOrders, 'blob');

const validateOrdersFromRecos = (storeIds, supplierId, startDate, endDate, userTimezone) =>
  APIcaller.apiPost('/order/orders-from-recommendations/validate', {
    storeIds,
    supplierId,
    startDate,
    endDate,
    userTimezone,
  });

const createOrdersFromRecos = (storeIds, supplierId, startDate, endDate, userTimezone) =>
  APIcaller.apiPost('/order/create-batch-from-recommendations', {
    storeIds,
    supplierId,
    startDate,
    endDate,
    userTimezone,
  });

const getOrderAvailabilities = (storeIds, supplierId, startDate, endDate) =>
  APIcallerv2.apiPost(`/order/availabilities`, {
    storeIds,
    supplierId,
    startDate,
    endDate,
  });

export default {
  getOrdersOfStores,
  getOrdersCountOfStores,
  getOrdersOfStoresPaginate,
  patchOrder,
  getOrdersAnalyticsByStore,
  getTurnoverByStoreIdsAndDates,
  sendOrderByEmail,
  deleteOrderById,
  cancelReceptionOrderById,
  getOrdersAnalyticsByCategory,
  getInventoryForStores,
  getOrderContentsExportDataByIds,
  getOrderAggregatedExportDataByIds,
  getOrderAnomaliesExportDataByIds,
  sendGroupedOrder,
  markOrdersAsDraft,
  markPreparationAsPreparing,
  markPreparationAsPrepared,
  getRecommandationsByBatch,
  getOrdersAnalyticsBySupplierProduct,
  getOrdersAnalyticsPricesEvolution,
  setCreditRequestProcessed,
  purchaseOrderPreview,
  creditOrderPreview,
  getOrderDetailsByOrderIds,
  setOrderToInPreparation,
  setOrderToPrepared,
  generateDeliveryNotePreview,
  checkCompaniesInformation,
  // Order batch import
  getOrderBatchCreationTemplate,
  ordersBatchCreationValidation,
  ordersBatchCreation,
  // Orders from recos
  validateOrdersFromRecos,
  createOrdersFromRecos,
  getOrderAvailabilities,
};
